import { cancelPollTasks, pollTasks, reloadItems } from './reloadItems';
import { preloadTaskPanelData } from '../actions/publicActions';
import { setFolderId, setSubject, setLinkedEmailEntity } from '../actions/quickAddActions';
import { processLoadedFolders } from '../actions/todoFolderStoreActions';
import { Datapoints, PerformanceDatapointCheckmarks } from '../datapoints';
import getSelectedFolderView from '../selectors/getSelectedFolderView';
import getTaskFoldersService from '../services/getTaskFoldersService';
import getTodoSettingsService from '../services/getTodoSettingsService';
import { TodoSettingType } from '../services/TodoSetting';
import { updateSettingService } from '../services/updateSettingsService';
import { ObservableMap } from 'mobx';
import { PerformanceDatapoint } from 'owa-analytics';
import { logUsage } from 'owa-analytics';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { orchestrator } from 'satcheljs';
import {
    loadPanel,
    setSelectedFolderId,
    setSelectedFolderView,
    panelLoaded,
    panelClosed,
    switchFolder,
    initializePanelTelemetryData,
    loadSettings,
    settingsLoaded,
    turnOnFlaggedEmailList,
} from '../actions/todoPanelStoreActions';

/**
 * Note about starting polling for task updates:
 * We start polls either when the panel opened or when folder is switched.
 * We do not start polls after preloading because we don't want to keep fetching if the panel isn't going to be opened.
 * When the panel is closed, we stop polling.
 */

export const switchFolderOrchestrator = orchestrator(switchFolder, async actionMessage => {
    const datapoint = new PerformanceDatapoint(
        Datapoints.todoPerformanceFolderSwitch.name,
        Datapoints.todoPerformanceFolderSwitch.options
    );

    // Setup polling to keep data updated.
    // Do this first (before waiting on reloadItems) because pollTasks in addition to setting up a new poll
    // also removes any previous polls.
    // Otherwise there's a small window in which previous timer may fire while we are reloading items for a new folder.
    pollTasks();

    setSelectedFolderId(actionMessage.folderId);
    await reloadItems(datapoint);

    logUsage(
        Datapoints.todoMetaDataTasks.name,
        Datapoints.todoMetaDataTasks.customData(),
        Datapoints.todoMetaDataTasks.options
    );
    datapoint.end();
});

export const setSelectedFolderIdOrchestrator = orchestrator(setSelectedFolderId, actionMessage => {
    let currentFolderView = getSelectedFolderView();

    // If the selected FolderView is not the current FolderView, create and set the current FolderView
    if (currentFolderView.folderId !== actionMessage.folderId) {
        currentFolderView = {
            folderId: actionMessage.folderId,
            activeTaskIds: [],
            recentlyCompletedTaskIds: [],
            todoItems: new ObservableMap(),
            folderViewState: {
                todoItemViewStates: new ObservableMap(),
                contextMenuViewState: null,
                currentEditingTodoId: '',
                focusTodoId: null,
                showCompletedTasks: false,
                dragToEndDropViewState: createDropViewState(),
                recentlyCompletedDropViewState: createDropViewState(),
            },
        };
        setSelectedFolderView(currentFolderView);

        // Update the folder id on the quickAdd view state
        setFolderId(actionMessage.folderId);
    }
});

export const loadPanelOrchestrator = orchestrator(loadPanel, async actionMessage => {
    initializePanelTelemetryData(actionMessage.source, actionMessage.parentSessionId);

    const datapoint = new PerformanceDatapoint(
        Datapoints.todoPerformancePanelLoad.name,
        Datapoints.todoPerformancePanelLoad.options
    );

    // setup polling to keep data updated
    // Do this now, instead of waiting after loadPanelData is done.
    pollTasks();

    await loadPanelData(datapoint);
    panelLoaded();
    datapoint.end();
});

export const panelClosedOrchestrator = orchestrator(panelClosed, () => {
    cancelPollTasks();
    setLinkedEmailEntity(null);
    setSubject('');
});

export const preloadTaskPanelDataOrchestrator = orchestrator(preloadTaskPanelData, async () => {
    await loadPanelData();
    // Note: do not start polling for task updates here. We don't want to keep firing requests
    // if the user isn't going to be using this folder.
});

async function loadPanelData(perfDatapoint?: PerformanceDatapoint) {
    await reloadItems(perfDatapoint);
    await loadFolders();
    if (isFeatureEnabled('todo-flagged-email-list')) {
        loadSettings();
    }
}

async function loadFolders(perfDatapoint?: PerformanceDatapoint) {
    const folders = await getTaskFoldersService();
    if (perfDatapoint) {
        perfDatapoint.addCheckmark(PerformanceDatapointCheckmarks.fetchedTaskFolders);
    }

    processLoadedFolders(folders);
    if (perfDatapoint) {
        perfDatapoint.addCheckmark(PerformanceDatapointCheckmarks.processedTaskFolders);

        // We only want to log metadata metric only for explicit loads, not a preload.
        // perfDatapoint is set to non-null only for these explicit loads.
        logUsage(
            Datapoints.todoMetaDataFolder.name,
            Datapoints.todoMetaDataFolder.customData(),
            Datapoints.todoMetaDataFolder.options
        );
    }
}

orchestrator(loadSettings, async () => {
    const settings = await getTodoSettingsService();
    if (settings && isSuccessStatusCode(settings.StatusCode)) {
        settingsLoaded(settings.ResponseBody);
    }
});

orchestrator(turnOnFlaggedEmailList, async () => {
    const flaggedSettingUpdateSuccess = await updateSettingService({
        Key: TodoSettingType.ListFlagged_Enabled,
        Value: 'true',
    });
    const flaggedTimeSettingUpdateSuccess = await updateSettingService({
        Key: TodoSettingType.ListFlagged_OnboardingLastDismissed,
        Value: new Date().toISOString(),
    });
    if (flaggedSettingUpdateSuccess && flaggedTimeSettingUpdateSuccess) {
        loadSettings();
        loadFolders();
    }
});
